import { useEffect, useState } from "react";
import { SelectableGroup } from "react-selectable-fast";

import * as Api from "@/api";
import GoTo from "@/app/shared/GoTo";
import { selectableColors } from "@/design/colors/colors";
import useNow from "@/utils/hooks/useNow";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import { getDays, timetableConditions } from "@/utils/timetable";

import { Box, Stack, Typography } from "@mui/material";
import { amber, blue, grey, lightGreen, lime, orange, yellow } from "@mui/material/colors";
import { useTheme } from "@mui/material/styles";
import {
  areIntervalsOverlapping,
  endOfISOWeek,
  getHours,
  getISODay,
  getMinutes,
  isAfter,
  isBefore,
  isSameSecond,
  startOfDay,
  startOfISOWeek,
  subSeconds,
} from "date-fns";
import PropTypes from "prop-types";

import conditionReducer from "./conditionReducer";
import HolidayCard from "./HolidayCard";
import Selectable from "./Selectable";

const quarterHeight = 10;
const hours = Array.from({ length: 24 }, (_, hour) => hour);
const quarters = Array.from({ length: 96 }, (_, quarter) => quarter);

function Roster({ timetableDispatch, timetable, activeDestination, getGoTo, ...props }) {
  const theme = useTheme();
  // The current-time marker on the roster has to keep moving while the page is open
  const today = useNow(60 * 1000);

  const [holidayHeight, setHolidayHeight] = useState(0);
  const [holidays, setHolidays] = useState({});

  const {
    value: conditions,
    dispatch: conditionsDispatch,
    setSaveState,
  } = useSave({
    initialValue: timetable.conditions,
    reducer: conditionReducer,
    delay: 300,
    onSubmit: () => Api.putTimetableCondition({ ...timetable, conditions }),
    onSuccess: () => {
      timetableDispatch({ type: "conditions", value: conditions });
    },
  });

  const currentTimetableConditions = timetableConditions({ ...timetable, conditions });

  // Get the active holidays
  useEffect(() => {
    async function getWeekHolidays() {
      const today = new Date();
      const _holidays = {
        mon: [],
        tue: [],
        wed: [],
        thu: [],
        fri: [],
        sat: [],
        sun: [],
      };
      const promises = [];

      timetable.holidaySets.map((holidaySet) => {
        const promise = Promise.resolve(Api.getHolidaySet(holidaySet)).then((response) => {
          response.data.holidaySet.holidays.forEach((holiday) => {
            holiday.start = new Date(holiday.start);
            holiday.end = new Date(holiday.end);

            // Check if this holiday (range) falls within the current week
            if (
              areIntervalsOverlapping(
                { start: startOfISOWeek(today), end: endOfISOWeek(today) },
                { start: holiday.start, end: holiday.end },
              )
            ) {
              let startDay = isBefore(holiday.start, startOfISOWeek(today)) ? 1 : getISODay(holiday.start);
              let endDay = isAfter(
                isSameSecond(holiday.end, startOfDay(holiday.end)) ? subSeconds(holiday.end, 1) : holiday.end,
                endOfISOWeek(today),
              )
                ? 7
                : getISODay(
                    isSameSecond(holiday.end, startOfDay(holiday.end)) ? subSeconds(holiday.end, 1) : holiday.end,
                  );

              for (let i = startDay; i <= endDay; i++) {
                _holidays[getDays()[i - 1]].push(holiday);
              }
            }
          });
        });

        promises.push(promise);
      });

      await Promise.all(promises)
        .then(() => {
          setHolidays(_holidays);
        })
        .finally(() => {
          let _holidayHeight = 0;

          Object.keys(_holidays).map(
            (day) => (_holidayHeight = _holidays[day].length > _holidayHeight ? _holidays[day].length : _holidayHeight),
          );

          setHolidayHeight(_holidayHeight);
        });
    }

    getWeekHolidays();
  }, [timetable.holidaySets]);

  function getDaysColumn() {
    const days = [];

    for (let i = 1; i < 8; i++) {
      // We have to add the number of hours passed because each hour causes an offset of 1px
      days.push({
        timePosition:
          getISODay(today) === i
            ? getHours(today) * quarterHeight * 4 + (getMinutes(today) / 60) * quarterHeight * 4 + getHours(today)
            : false,
        day: getDays()[i - 1],
      });
    }

    return days;
  }

  // Handle changes
  const handleHourClicked = (hour, position) => {
    conditionsDispatch({ type: "hour", hour, position, destination: activeDestination });

    setSaveState(SaveState.UNSAVED);
    props.setSaveState(SaveState.UNSAVED);
  };

  const handleDayClicked = (day) => {
    conditionsDispatch({ type: "day", day, destination: activeDestination });

    setSaveState(SaveState.UNSAVED);
    props.setSaveState(SaveState.UNSAVED);
  };

  const handleSelectionFinish = (selection) => {
    if (selection.length > 0) {
      conditionsDispatch({ type: "selection", selection, destination: activeDestination });

      setSaveState(SaveState.UNSAVED);
      props.setSaveState(SaveState.UNSAVED);
    }
  };

  return (
    <>
      {/* Day markers */}
      <Box sx={{ display: "flex" }}>
        <Box sx={{ minWidth: { xs: 56, lg: 72 }, alignItems: "stretch" }} />
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex" }}>
            {getDays().map((day, key) => (
              <Box
                key={day}
                id={`day-${day}`}
                onClick={() => handleDayClicked(day)}
                sx={{
                  flexGrow: "1",
                  height: 48,
                  position: "relative",
                  transition: "all .2s ease-in",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <Typography
                  sx={[
                    {
                      color: "primary.500",
                      position: "relative",
                      textTransform: "capitalize",
                      textAlign: "center",
                      display: "block",
                      fontWeight: 700,
                      borderRadius: 100,
                      fontSize: 18,
                      my: 0.875,
                      mx: "auto",
                      width: 28,
                      "&::after": {
                        content: "''",
                        backgroundColor: "primary.100",
                        width: 16,
                        height: 4,
                        borderRadius: 3,
                        position: "absolute",
                        bottom: -3,
                        left: `calc(50% - 8px)`,
                      },
                    },
                    getISODay(today) === key + 1 && {
                      "&::after": {
                        backgroundColor: "primary.500",
                      },
                    },
                  ]}
                >
                  {day.substring(0, 2)}
                </Typography>
                <Box
                  sx={[
                    {
                      height: 18,
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      width: 1,
                    },
                    key <= 3 && {
                      borderLeft: `1px solid ${theme.vars.palette.divider}`,
                    },
                    key >= 3 && {
                      borderRight: `1px solid ${theme.vars.palette.divider}`,
                    },
                  ]}
                />
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ minWidth: { xs: 56, lg: 72 }, alignItems: "stretch" }} />
      </Box>
      {/* Holiday cards */}
      <Box sx={{ display: "flex" }}>
        <Box sx={{ minWidth: { xs: 56, lg: 72 }, alignItems: "stretch" }} />
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex" }}>
            {getDays().map((daySet, key) => (
              <Box
                style={{
                  flexGrow: "1",
                  position: "relative",
                  height: 55.8 * holidayHeight + 1 * holidayHeight,
                }}
                key={daySet}
              >
                <Stack
                  sx={[
                    {
                      position: "absolute",
                      top: 0,
                      width: 1,
                      height: 1,
                      borderBottom: (theme) => `1px solid ${theme.vars.palette.divider}`,
                      gap: 0.125,
                      px: 0.125,
                    },
                    key <= 3 && {
                      borderLeft: (theme) => `1px solid ${theme.vars.palette.divider}`,
                    },
                    key >= 3 && {
                      borderRight: (theme) => `1px solid ${theme.vars.palette.divider}`,
                    },
                  ]}
                >
                  {holidays?.[daySet]?.map((holiday, _key) => (
                    <HolidayCard key={_key} day={key} holiday={holiday} getGoTo={getGoTo} />
                  ))}
                </Stack>
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ minWidth: { xs: 56, lg: 72 }, alignItems: "stretch" }} />
      </Box>
      {/* Main timetable */}
      <Box sx={{ display: "flex" }}>
        <Box sx={{ minWidth: { xs: 56, lg: 72 }, alignItems: "stretch" }}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {hours.map((hour) => (
              <Box
                sx={(theme) => ({
                  height: quarterHeight * 4 + 1,
                  position: "relative",
                  transition: "all .2s ease-in",
                  cursor: "pointer",
                  color: "#aaa",
                  ":hover": {
                    backgroundColor: "action.hover",
                    color: "primary.500",
                    ...theme.applyStyles("dark", {
                      color: "primary.50",
                    }),
                  },
                })}
                key={hour}
                id={`week-${hour}`}
                onClick={() => handleHourClicked(hour, "week")}
              >
                <Typography
                  sx={{
                    color: "inherit",
                    display: "inline-block",
                    top: -9,
                    right: { xs: 12, lg: 16 },
                    position: "absolute",
                    fontSize: 12,
                  }}
                >
                  {`${hour < 10 ? "0" : ""}`}
                  {hour}:00
                </Typography>
                <Box
                  sx={[
                    {
                      borderBottom: (theme) => `1px solid ${theme.vars.palette.divider}`,
                      mt: -0.125,
                      width: 12,
                      position: "absolute",
                      right: { xs: 6, lg: 0 },
                    },
                    hour === 0 &&
                      holidayHeight === 0 && {
                        mt: 0,
                      },
                  ]}
                />
              </Box>
            ))}
          </Box>
        </Box>
        <SelectableGroup
          clickClassName="tick"
          allowClickWithoutSelected={true}
          onSelectionFinish={handleSelectionFinish}
          resetOnStart={true}
          style={{ flex: 1 }}
        >
          <Box sx={{ display: "flex" }}>
            {getDaysColumn().map((dayCol) => (
              <Box style={{ flexGrow: "1", position: "relative" }} key={dayCol.day}>
                <Box style={{ display: "flex", flexFlow: "column" }}>
                  {hours.map((i) => (
                    <Box
                      key={i}
                      sx={[
                        {
                          borderLeft: (theme) => `1px solid ${theme.vars.palette.divider}`,
                          borderBottom: (theme) => `1px solid ${theme.vars.palette.divider}`,
                        },
                        dayCol.day === "sun" && {
                          borderRight: (theme) => `1px solid ${theme.vars.palette.divider}`,
                        },
                      ]}
                    >
                      <Box
                        sx={(theme) => ({
                          height: quarterHeight,
                          "&:not(:first-of-type)": {
                            borderTop: `1px solid #f3f3f3`,
                            ...theme.applyStyles("dark", {
                              borderTopColor: "#25252d",
                            }),
                          },
                        })}
                      />
                      <Box
                        sx={(theme) => ({
                          height: quarterHeight,
                          "&:not(:first-of-type)": {
                            borderTop: `1px solid #f3f3f3`,
                            ...theme.applyStyles("dark", {
                              borderTopColor: "#25252d",
                            }),
                          },
                        })}
                      />
                      <Box
                        sx={(theme) => ({
                          height: quarterHeight,
                          "&:not(:first-of-type)": {
                            borderTop: `1px solid #f3f3f3`,
                            ...theme.applyStyles("dark", {
                              borderTopColor: "#25252d",
                            }),
                          },
                        })}
                      />
                      <Box
                        sx={(theme) => ({
                          height: quarterHeight,
                          "&:not(:first-of-type)": {
                            borderTop: `1px solid #f3f3f3`,
                            ...theme.applyStyles("dark", {
                              borderTopColor: "#25252d",
                            }),
                          },
                        })}
                      />
                    </Box>
                  ))}
                </Box>
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    width: { xs: "calc(100% - 3px)", lg: "calc(100% - 13px)" },
                    height: 1,
                    left: 1,
                  }}
                >
                  {currentTimetableConditions[dayCol.day]?.map((condition, key) => {
                    let conditionColor = selectableColors.find((color) => condition.rule.color === color[500]) || blue;
                    const useContrast =
                      [lime[500], yellow[500], amber[500], orange[500], lightGreen[500], grey[500]].indexOf(
                        conditionColor[500],
                      ) >= 0;

                    return (
                      <Box
                        key={key}
                        sx={{
                          my: 0,
                          mx: 0.25,
                          py: 0.365,
                          px: 0.75,
                          borderRadius: 1,
                          position: "absolute",
                          left: 0,
                          right: 0,
                          overflow: "hidden",
                          background: `linear-gradient(135deg, ${conditionColor[300]} 0%, ${conditionColor[400]} 100%)`,
                          top: (quarterHeight * 4 + 1) * condition.rstart,
                          height: quarterHeight * 4 * condition.duration + condition.duration - 3,
                        }}
                      >
                        {condition.duration >= 1 && (
                          <>
                            <Typography
                              sx={{
                                color: useContrast ? "#000" : "#fff",
                                fontSize: 12,
                                lineHeight: 1.2,
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {condition.start} - {condition.end}
                            </Typography>
                            <Typography
                              sx={{
                                color: useContrast ? "#000" : "#fff",
                                fontSize: 14,
                                fontWeight: 600,
                                lineHeight: 1.2,
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              <GoTo target={getGoTo(condition.rule.overflow)} hideType />
                            </Typography>
                          </>
                        )}
                      </Box>
                    );
                  })}
                </Box>
                {dayCol.timePosition && (
                  <Box
                    sx={{
                      position: "absolute",
                      width: 1,
                      height: 10,
                      top: dayCol.timePosition,
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: "primary.500",
                        borderRadius: 6,
                        position: "absolute",
                        width: 12,
                        height: 12,
                        ml: -0.75,
                        mt: -0.625,
                        zIndex: "600",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        zIndex: "600",
                        borderTop: (theme) => `${theme.vars.palette.primary[500]} solid 2px`,
                        left: 0,
                        right: 0,
                        pointerEvents: "none",
                      }}
                    />
                  </Box>
                )}
                {!!timetable.id && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      width: 1,
                      height: 1,
                    }}
                  >
                    {quarters.map((cel) => (
                      <Selectable key={cel} place={cel} day={dayCol.day} />
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </SelectableGroup>
        <Box sx={{ minWidth: { xs: 56, lg: 72 }, alignItems: "stretch" }}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {hours.map((hour) => (
              <Box
                sx={{
                  height: quarterHeight * 4 + 1,
                  position: "relative",
                  transition: "all .2s ease-in",
                  cursor: "pointer",
                  color: "#aaa",
                  ":hover": {
                    backgroundColor: "action.hover",
                    color: "primary.500",
                    ...theme.applyStyles("dark", {
                      color: "primary.50",
                    }),
                  },
                }}
                key={hour}
                id={`weekend-${hour}`}
                onClick={() => handleHourClicked(hour, "weekend")}
              >
                <Typography
                  sx={{
                    color: "inherit",
                    display: "inline-block",
                    top: -9,
                    left: { xs: 12, lg: 16 },
                    position: "absolute",
                    fontSize: 12,
                  }}
                >
                  {`${hour < 10 ? "0" : ""}`}
                  {hour}:00
                </Typography>
                <Box
                  sx={[
                    {
                      borderBottom: (theme) => `1px solid ${theme.vars.palette.divider}`,
                      mt: -0.125,
                      width: 12,
                      position: "absolute",
                      left: 0,
                    },
                    hour === 0 &&
                      holidayHeight === 0 && {
                        mt: 0,
                      },
                  ]}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </>
  );
}

Roster.propTypes = {
  timetable: PropTypes.object,
  activeDestination: PropTypes.object,
  getGoTo: PropTypes.func,
  timetableDispatch: PropTypes.func,
  setSaveState: PropTypes.func,
};

export default Roster;
